import { OrganizationNames } from '../../assets/images/cuisines/content'

const modKitchenTermsOfUse = `
# MOD Kitchen – Terms and Conditions

**Effective Date:** March 15th, 2025 **Last Updated:** March 15th, 2025

## 1. Introduction

Welcome to **MOD Kitchen** ("we," "our," or "us"). By accessing and using **[www.modkitchen.ca]** (the "Website"), you agree to be bound by these **Terms and Conditions** ("Terms"). If you do not agree with any part of these Terms, please do not use our services.

## 2. Eligibility & User Responsibilities

* You must be **at least 18 years old** or have parental/guardian consent to use this Website.
* You agree to use this Website **only for lawful purposes** and in compliance with all applicable laws in **Canada**.
* You are responsible for ensuring that any **account information** you provide is accurate and up to date.

## 3. Ordering & Payment Policy

* All orders placed through MOD Kitchen are subject to availability. We reserve the right to **cancel or refuse orders** at our discretion.
* Prices listed on the Website **exclude applicable taxes** and are subject to change without notice.
* Payments must be made **at the time of ordering** via accepted payment methods. We use third-party payment processors to secure transactions.
* If there are issues with your order (e.g., incorrect items, missing products), please **contact us within 24 hours** of receiving your order.

## 4. Refund & Cancellation Policy

* Orders can only be canceled if the request is made **before preparation begins**.
* Refunds are granted on a **case-by-case basis** at MOD Kitchen's discretion.
* If your order arrives **incorrect, incomplete, or damaged**, please provide **proof (e.g., photos)** and reach out to our support team for resolution.

## 5. Privacy & Data Protection

* MOD Kitchen complies with **Canada's Personal Information Protection and Electronic Documents Act (PIPEDA)**.
* By using this Website, you agree to the **collection, storage, and processing of your personal information** in accordance with our **[Privacy Policy]**.
* We do not **sell, trade, or rent** your personal information to third parties without your consent.

## 6. Intellectual Property Rights

* All **content, logos, images, and text** on the Website are the property of MOD Kitchen or our licensors.
* You may **not copy, distribute, or reproduce** any material from this Website without our written consent.

## 7. Third-Party Links & Services

* Our Website may contain links to **third-party websites** (e.g., delivery platforms, payment processors). We are **not responsible** for their content, policies, or practices.
* If you choose to interact with third-party services, you do so at your **own risk**.

## 8. Limitation of Liability

* MOD Kitchen is **not liable** for any **indirect, incidental, or consequential damages** arising from: 
   * Use of or inability to use our Website.
   * Service delays, technical errors, or system failures.
   * Errors in product descriptions, pricing, or availability.
* Our liability, under any circumstances, **shall not exceed the amount paid for your order**.

## 9. Modifications & Updates

* MOD Kitchen reserves the right to **update or modify** these Terms at any time.
* Any changes will be effective immediately upon posting on the Website.
* It is your responsibility to review these Terms periodically.

## 10. Governing Law

These Terms are governed by the **laws of British Columbia, Canada**. Any disputes shall be subject to the exclusive jurisdiction of the courts in Vancouver, BC.

## 11. Contact Information

For any inquiries regarding these Terms, please contact us:

📧 **Email:** info@modkitchen.ca  
📞 **Phone:** (604) 238-1637  
📍 **Address:** Unit 15 & 16 Horseshoe Way, Richmond, BC
`

const defaultTermsOfUse = `
# Terms of Service

**Last Modified:** July 7th, 2025  
**Effective Date:** July 7th, 2025

## 1. Agreement to Terms

These Terms of Service ("Agreement") govern your use of {{companyName}}'s website located at {{websiteUrl}} ("Service") operated by {{companyName}} ("Company," "we," "us," or "our"). By accessing or using our Service, you acknowledge that you have read, understood, and agree to be legally bound by this Agreement.

## 2. User Requirements and Conduct

- Users must be 18 years of age or older, or have valid parental/guardian authorization to use this Service
- You commit to using our Service exclusively for legitimate, lawful activities in accordance with all applicable local, provincial, and federal regulations
- You are obligated to maintain accurate, current, and complete information in your user profile and account details
- Any violation of these conduct requirements may result in immediate suspension or termination of access

## 3. Orders and Payment Terms

- Order acceptance is contingent upon product availability and our internal verification processes. We maintain the right to decline or cancel any order for any reason
- All pricing displayed on our platform is exclusive of applicable taxes, shipping fees, and other charges, and may be modified without prior notification
- Full payment is required upon order submission through our approved payment gateway partners, who handle all transaction security
- Customers experiencing order discrepancies, missing items, or quality issues must notify us within 24 hours of delivery for prompt resolution

## 4. Cancellation and Return Policy

- Order modifications or cancellations are only permitted prior to the commencement of order processing and fulfillment
- Return eligibility and refund approvals are evaluated individually based on specific circumstances and company policies
- Claims for defective, incorrect, or incomplete orders require supporting documentation (such as photographs) and must be submitted to our customer service team for review and resolution

## 5. Data Privacy and Protection

- Our data handling practices comply with applicable Canadian privacy legislation, including the Personal Information Protection and Electronic Documents Act (PIPEDA)
- Your use of this Service constitutes consent to our data collection, storage, and processing practices as detailed in our Privacy Policy
- We maintain strict confidentiality of customer information and do not share, sell, or distribute personal data to external parties without explicit user consent

## 6. Intellectual Property Protection

- All website content, including but not limited to text, graphics, logos, images, and software, remains the exclusive property of {{companyName}} and our content partners
- Users are prohibited from reproducing, distributing, modifying, or creating derivative works from any proprietary content without express written authorization

## 7. External Links and Third-Party Services

- Our platform may include links to external websites and services (including payment processors, delivery partners, and other vendors) for user convenience
- We disclaim responsibility for the content, privacy practices, terms of service, or security measures of any linked third-party platforms
- Your interactions with external services are conducted at your own discretion and risk

## 8. Liability Limitations and Disclaimers

To the maximum extent permitted by law, {{companyName}} shall not be held liable for:
- Any indirect, consequential, punitive, or incidental damages resulting from Service use or inability to access the Service
- Technical malfunctions, server downtime, connectivity issues, or system maintenance periods  
- Inaccuracies in product information, inventory levels, or pricing displays
- Service interruptions caused by factors beyond our reasonable control

Our total liability under any circumstances is limited to the monetary value of your most recent transaction with us.

## 9. Terms Modification Rights

- {{companyName}} reserves the authority to revise, update, or replace these Terms of Service at any time without advance notice
- Updated terms become effective immediately upon publication on our website
- Users are responsible for periodically reviewing these terms to stay informed of any changes
- Continued use of our Service following term modifications constitutes acceptance of the revised agreement

## 10. Legal Jurisdiction

This Agreement is governed by and interpreted in accordance with the laws of {{province}}, Canada. Any legal disputes or claims arising from this Agreement shall be resolved exclusively through the court system of {{city}}, {{province}}.

## 11. Customer Support

For questions, concerns, or clarification regarding these Terms of Service, please reach out to our support team:

**Email:** {{contactEmail}}  
**Phone:** {{contactPhone}}  
**Mailing Address:** {{physicalAddress}}

---

*This document was last updated on {{lastUpdated}} and supersedes all previous versions.*
`

// Function to replace placeholders with dynamic values
export const replaceTermsOfUsePlaceholders = (content: string, orgData: any) => {
	if (!orgData) return content

	const replacements = {
		companyName: orgData.name || 'Our Company',
		websiteUrl: orgData.webURL || 'our website',
		province: orgData.province || 'British Columbia',
		city: orgData.city || 'Vancouver',
		contactEmail: orgData.email || 'support@company.com',
		contactPhone: orgData.phoneNumber || '(555) 123-4567',
		physicalAddress: orgData.address || '123 Main Street, City, Province',
		lastUpdated: 'July 7th, 2025',
	}

	let processedContent = content
	Object.entries(replacements).forEach(([key, value]) => {
		const placeholder = `{{${key}}}`
		processedContent = processedContent.replace(new RegExp(placeholder, 'g'), value)
	})

	return processedContent
}

export const ORGANIZATION_TERMS_OF_USE_MAP = {
	[OrganizationNames.ModKitchen]: modKitchenTermsOfUse,
	default: defaultTermsOfUse,
}
